import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputBase,
  Paper,
  Popover,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import { apiFetch } from "../../../api/apiFetch";
import LoadingState from "../../common/LoadingState";
import { trustBudTokens } from "../../../theme/tokens";

const HEALTH_PATTERN = /(health|fitness|weight|walk|walking|run|running|gym|workout|exercise|eat|eating|nutrition|sleep)/i;
const ROUTINE_PATTERN = /(routine|habit|daily|consistent|consistency|show up|showing up|momentum)/i;
const ACCOUNTABILITY_PATTERN = /(accountab|check in|check-in|support|follow through|follow-through)/i;
const START_PATTERN = /(open|ready|start|restart|begin)/i;

function cardShellSx(extra = {}) {
  return {
    backgroundColor: "#ffffff",
    borderRadius: "14px",
    border: "1px solid rgba(215, 226, 238, 0.94)",
    boxShadow: "none",
    ...extra,
  };
}

function cleanText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function splitSupportingText(value) {
  return cleanText(value)
    .split(/\s*[•·|/]\s*/)
    .map((part) => cleanText(part))
    .filter(Boolean);
}

function inferIntent(candidate) {
  const source = `${cleanText(candidate?.focus_area)} ${cleanText(candidate?.headline)}`;
  if (ACCOUNTABILITY_PATTERN.test(source)) return "Looking for accountability";
  if (HEALTH_PATTERN.test(source)) return "Focused on fitness";
  if (ROUTINE_PATTERN.test(source)) return "Trying to build a routine";
  if (candidate?.activity_tone === "active" || candidate?.activity_tone === "recent") {
    return "Working on consistency";
  }
  if (START_PATTERN.test(source)) return "Building daily habits";
  return "Building daily habits";
}

function resolveGoalTitle(candidate) {
  const focusArea = cleanText(candidate?.focus_area);
  const headline = cleanText(candidate?.headline);
  if (focusArea) return focusArea;
  const headlineParts = splitSupportingText(headline);
  if (headlineParts.length) return headlineParts[0];
  if (headline) return headline;
  return inferIntent(candidate);
}

function addUniqueTag(tags, value) {
  const tag = cleanText(value);
  if (tag && !tags.includes(tag)) tags.push(tag);
}

function buildTags(candidate, sectionKey, goalTitle) {
  const tags = [];
  const source = `${cleanText(candidate?.focus_area)} ${cleanText(candidate?.headline)}`;

  if (sectionKey === "good_matches") {
    addUniqueTag(tags, "Similar goal");
  }

  if (HEALTH_PATTERN.test(source)) addUniqueTag(tags, "Health");
  if (ROUTINE_PATTERN.test(source)) addUniqueTag(tags, "Consistency");
  if (ACCOUNTABILITY_PATTERN.test(source)) addUniqueTag(tags, "Accountability");
  if (candidate?.activity_tone === "active") addUniqueTag(tags, "Active today");
  if (candidate?.activity_tone === "recent") addUniqueTag(tags, "This week");

  const intent = inferIntent(candidate);
  if (!tags.length && intent !== goalTitle) {
    addUniqueTag(tags, intent.replace(/^Looking for /, "").replace(/^Working on /, ""));
  }

  return tags.slice(0, 3);
}

function buildReasonLine(candidate) {
  const reasons = (candidate?.match_reasons || []).map(cleanText).filter(Boolean);
  const fallback = (candidate?.tags || []).map(cleanText).filter(Boolean);
  return (reasons.length ? reasons : fallback).slice(0, 3).join(" · ");
}

function normalizeCandidate(candidate, sectionKey) {
  const activityStatus = cleanText(candidate?.activity_status) || "Quiet lately";
  const canRequest =
    candidate?.can_request_connection !== undefined
      ? candidate.can_request_connection !== false
      : candidate?.can_request_sprint !== false;
  const normalized = {
    ...candidate,
    sectionKey,
    name: cleanText(candidate?.display_name || candidate?.name) || "TrustBud",
    avatar: candidate?.avatar || null,
    activity_status: activityStatus,
    activity_tone:
      candidate?.activity_tone ||
      (/today/i.test(activityStatus)
        ? "active"
        : /week|yesterday|recent/i.test(activityStatus)
          ? "recent"
          : "quiet"),
    focus_area: cleanText(candidate?.focus_area),
    headline: cleanText(candidate?.headline),
    can_request_connection: canRequest,
  };
  const goalTitle = resolveGoalTitle(normalized);
  return {
    ...normalized,
    goal_title: goalTitle,
    tags: buildTags(normalized, sectionKey, goalTitle),
  };
}

function getSectionTone(sectionKey) {
  if (sectionKey === "good_matches") {
    return {
      shell: {
        background: "linear-gradient(180deg, #f5f9ff 0%, #ffffff 100%)",
        border: "1px solid rgba(37, 75, 121, 0.18)",
      },
      header: "#163560",
      eyebrowBg: "#e6f0ff",
      eyebrowColor: "#224c7a",
    };
  }
  if (sectionKey === "recently_active") {
    return {
      shell: {
        background: "#ffffff",
        border: "1px solid rgba(215, 226, 238, 0.94)",
      },
      header: "#13233d",
      eyebrowBg: "#f1f5f9",
      eyebrowColor: "#51657e",
    };
  }
  return {
    shell: {
      background: "#fbfcfe",
      border: "1px solid rgba(223, 232, 241, 0.94)",
    },
    header: "#314761",
    eyebrowBg: "#f4f7fa",
    eyebrowColor: "#60738c",
  };
}

function FilterChip({ label, active, onClick }) {
  return (
    <Button
      onClick={onClick}
      variant="text"
      sx={{
        minHeight: 34,
        px: 1.35,
        borderRadius: 999,
        border: "1px solid",
        borderColor: active ? "rgba(37, 75, 121, 0.22)" : "rgba(23, 52, 93, 0.14)",
        backgroundColor: active ? "#eef4ff" : "#f6f8fb",
        color: active ? trustBudTokens.colors.brand.navy700 : "#54667b",
        fontWeight: active ? 800 : 700,
        textTransform: "none",
        lineHeight: 1,
        "&:hover": {
          backgroundColor: active ? "#e5efff" : "#eef3f8",
        },
      }}
    >
      {label}
    </Button>
  );
}

function CandidateRow({ candidate, onPrimaryPress, sending }) {
  const buttonLabel =
    candidate.can_request_connection === false ? "Unavailable" : "Request TrustBud";
  const activityTone =
    candidate.activity_tone === "quiet"
      ? "#7a8798"
      : candidate.activity_tone === "recent"
        ? "#b36919"
        : "#1b6a48";

  return (
    <Box
      sx={{
        px: { xs: 1.25, md: 1.45 },
        py: { xs: 1, md: 1.05 },
        borderTop: "1px solid rgba(232, 238, 246, 0.94)",
        transition: "transform 140ms ease, box-shadow 140ms ease, background-color 140ms ease",
        "&:first-of-type": {
          borderTop: "none",
        },
        "&:hover": {
          backgroundColor: "#f8fbff",
          transform: "translateY(-1px)",
          boxShadow: "inset 0 0 0 1px rgba(37, 75, 121, 0.04)",
        },
      }}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 1.2, alignItems: "center" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "42px minmax(0, 1fr)", gap: 0.9, alignItems: "center", minWidth: 0 }}>
          <Avatar
            src={candidate.avatar || ""}
            sx={{ width: 38, height: 38, bgcolor: "#dbe6f1", color: "#13233d", fontWeight: 800 }}
          >
            {candidate.name?.slice(0, 1)?.toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.98rem", fontWeight: 800, color: "#13233d", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {candidate.name}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          disabled={sending || candidate.can_request_connection === false}
          startIcon={sending ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : null}
          onClick={() => onPrimaryPress(candidate)}
          sx={{
            minWidth: { xs: 112, md: 126 },
            minHeight: 34,
            px: 1.25,
            borderRadius: "11px",
            backgroundColor: trustBudTokens.colors.brand.navy700,
            boxShadow: "none",
            fontWeight: 800,
            fontSize: "0.82rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: trustBudTokens.colors.brand.navy800,
              boxShadow: "0 8px 18px rgba(23, 52, 93, 0.16)",
            },
          }}
        >
          {buttonLabel}
        </Button>
      </Box>

      <Typography sx={{ mt: 0.35, ml: "51px", fontSize: "0.78rem", color: activityTone, fontWeight: 700, lineHeight: 1.25 }}>
        {candidate.activity_status}
      </Typography>
      <Typography sx={{ mt: 0.35, ml: "51px", fontSize: "0.98rem", fontWeight: 800, color: "#13233d", lineHeight: 1.24 }}>
        {candidate.goal_title}
      </Typography>
      {buildReasonLine(candidate) ? (
        <Typography sx={{ mt: 0.3, ml: "51px", fontSize: "0.8rem", color: "#60738c", fontWeight: 700, lineHeight: 1.25 }}>
          {buildReasonLine(candidate)}
        </Typography>
      ) : null}
    </Box>
  );
}

function CandidateSection({ sectionKey, title, candidates, sendingId, onPrimaryPress }) {
  if (!candidates?.length) return null;
  const tone = getSectionTone(sectionKey);

  return (
    <Box
      sx={cardShellSx({
        overflow: "hidden",
        ...tone.shell,
      })}
    >
      <Box sx={{ px: 1.45, pt: 1.1, pb: 0.35, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography sx={{ fontSize: "1.02rem", fontWeight: 800, color: tone.header }}>
          {title}
        </Typography>
        <Box
          sx={{
            px: 0.95,
            py: 0.36,
            borderRadius: 999,
            backgroundColor: tone.eyebrowBg,
            color: tone.eyebrowColor,
            fontSize: "0.74rem",
            fontWeight: 800,
            lineHeight: 1,
          }}
        >
          {candidates.length}
        </Box>
      </Box>
      <Box>
        {candidates.map((candidate) => (
          <CandidateRow
            key={`${sectionKey}-${candidate.id}`}
            candidate={candidate}
            onPrimaryPress={onPrimaryPress}
            sending={sendingId === candidate.id}
          />
        ))}
      </Box>
    </Box>
  );
}

function RefinePeoplePanel({
  anchorEl,
  onClose,
  activity,
  onActivityChange,
  goalArea,
  onGoalAreaChange,
  goalAreas,
}) {
  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Box sx={{ p: 2, width: 316, display: "grid", gap: 1.4 }}>
        <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: "#13233d" }}>
          Refine
        </Typography>

        <Box>
          <Typography sx={{ mb: 0.8, fontSize: "0.8rem", fontWeight: 800, color: "#60738c" }}>
            Activity
          </Typography>
          <Box sx={{ display: "flex", gap: 0.7, flexWrap: "wrap" }}>
            {[
              { key: "all", label: "All" },
              { key: "active", label: "Active today" },
              { key: "recent", label: "Active this week" },
              { key: "quiet", label: "Quiet lately" },
            ].map((option) => (
              <FilterChip
                key={option.key}
                label={option.label}
                active={activity === option.key}
                onClick={() => onActivityChange(option.key)}
              />
            ))}
          </Box>
        </Box>

        {goalAreas.length ? (
          <Box>
            <Typography sx={{ mb: 0.8, fontSize: "0.8rem", fontWeight: 800, color: "#60738c" }}>
              Goal
            </Typography>
            <Box sx={{ display: "flex", gap: 0.7, flexWrap: "wrap" }}>
              <FilterChip
                label="All"
                active={goalArea === ""}
                onClick={() => onGoalAreaChange("")}
              />
              {goalAreas.map((area) => (
                <FilterChip
                  key={area}
                  label={area}
                  active={goalArea === area}
                  onClick={() => onGoalAreaChange(area)}
                />
              ))}
            </Box>
          </Box>
        ) : null}
      </Box>
    </Popover>
  );
}

function normalizePayload(result) {
  const sections = {
    good_matches: (result?.good_matches || []).map((candidate) => normalizeCandidate(candidate, "good_matches")),
    recently_active: (result?.recently_active || []).map((candidate) => normalizeCandidate(candidate, "recently_active")),
    other_people: (result?.other_people || []).map((candidate) => normalizeCandidate(candidate, "other_people")),
  };

  return {
    ...result,
    ...sections,
  };
}

export default function FindPeopleTab({ onOpenInvite, onRequestSent }) {
  const [query, setQuery] = useState("");
  const [activity, setActivity] = useState("all");
  const [goalArea, setGoalArea] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sendingId, setSendingId] = useState(null);
  const [feedback, setFeedback] = useState("");

  const loadDiscovery = useCallback(
    async ({ nextOffset = 0, append = false } = {}) => {
      setLoading(!append);
      setError("");
      try {
        const params = new URLSearchParams();
        if (query.trim()) params.set("query", query.trim());
        if (activity && activity !== "all") params.set("activity", activity);
        if (goalArea) params.set("goal_area", goalArea);
        params.set("offset", String(nextOffset));
        params.set("limit", "24");
        const result = await apiFetch(`/connections/discovery/?${params.toString()}`);
        if (!result) {
          throw new Error("Discovery unavailable");
        }
        const normalized = normalizePayload(result);
        setPayload((prev) => {
          if (!append || !prev) return normalized;
          return {
            ...normalized,
            good_matches: [...(prev.good_matches || []), ...(normalized.good_matches || [])],
            recently_active: [...(prev.recently_active || []), ...(normalized.recently_active || [])],
            other_people: [...(prev.other_people || []), ...(normalized.other_people || [])],
          };
        });
      } catch (err) {
        setPayload(null);
        setError(err?.message || "Unable to load people right now.");
      } finally {
        setLoading(false);
      }
    },
    [activity, goalArea, query]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      loadDiscovery({ nextOffset: 0, append: false });
    }, 180);
    return () => clearTimeout(timer);
  }, [loadDiscovery]);

  const goalAreas = useMemo(() => payload?.filters?.goal_areas || [], [payload]);
  const sectionLabels = payload?.section_labels || {};

  const handlePrimaryPress = useCallback(
    async (candidate) => {
      if (candidate.can_request_connection === false) return;
      setSendingId(candidate.id);
      try {
        await apiFetch("/connections/request/", {
          method: "POST",
          body: JSON.stringify({ to_user_id: candidate.id }),
        });
        setFeedback(`Request sent to ${candidate.name}.`);
        onRequestSent?.();
        loadDiscovery({ nextOffset: 0, append: false });
      } catch (err) {
        setError(err?.message || "Unable to send request.");
      } finally {
        setSendingId(null);
      }
    },
    [loadDiscovery, onRequestSent]
  );

  const hasResults =
    (payload?.good_matches || []).length > 0 ||
    (payload?.recently_active || []).length > 0 ||
    (payload?.other_people || []).length > 0;

  return (
    <Box sx={{ maxWidth: 1120 }}>
      <Box sx={{ display: "flex", alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2.4 }}>
        <Box>
          <Typography sx={{ fontSize: "2.5rem", lineHeight: 1.04, fontWeight: 800, color: "#13233d" }}>
            Find people
          </Typography>
          <Typography sx={{ mt: 0.7, fontSize: "1rem", color: "#51657e", lineHeight: 1.4 }}>
            Search for someone specific, browse people with similar goals, or invite someone you already know.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<LinkRoundedIcon sx={{ fontSize: 18 }} />}
          onClick={onOpenInvite}
          sx={{
            minHeight: 42,
            px: 1.75,
            borderRadius: "14px",
            borderColor: "rgba(23, 52, 93, 0.16)",
            color: trustBudTokens.colors.brand.navy700,
            fontWeight: 800,
            textTransform: "none",
            "&:hover": {
              borderColor: "rgba(23, 52, 93, 0.26)",
              backgroundColor: "#f8fbff",
            },
          }}
        >
          Invite by link
        </Button>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" }, gap: 1, alignItems: "center", mb: 1.9 }}>
        <Paper
          elevation={0}
          sx={cardShellSx({
            px: 1.4,
            minHeight: 48,
            display: "grid",
            gridTemplateColumns: "18px minmax(0, 1fr) auto",
            alignItems: "center",
            gap: 0.95,
          })}
        >
          <SearchRoundedIcon sx={{ fontSize: 19, color: "#60738c" }} />
          <InputBase
            placeholder="Search name, username, goal, or exact email"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{
              fontSize: "0.96rem",
              color: "#13233d",
              "& input::placeholder": { color: "#60738c", opacity: 1 },
            }}
          />
          {query.trim() ? (
            <IconButton
              aria-label="Clear search"
              size="small"
              onClick={() => setQuery("")}
              sx={{ color: "#60738c" }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Paper>
        <Button
          variant="outlined"
          startIcon={<FilterListRoundedIcon sx={{ fontSize: 18 }} />}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            minHeight: 42,
            px: 1.45,
            borderRadius: "14px",
            fontWeight: 800,
            textTransform: "none",
            borderColor: "rgba(23, 52, 93, 0.16)",
            color: trustBudTokens.colors.brand.navy700,
          }}
        >
          Refine
        </Button>
      </Box>

      <RefinePeoplePanel
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        activity={activity}
        onActivityChange={setActivity}
        goalArea={goalArea}
        onGoalAreaChange={setGoalArea}
        goalAreas={goalAreas}
      />

      {feedback ? (
        <Alert severity="success" sx={{ mb: 1.8 }}>
          {feedback}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 1.8 }}>
          {error}
        </Alert>
      ) : null}

      {loading && !payload ? (
        <LoadingState
          title="Finding good matches"
          body="Checking searchable people, goal fit, and recent activity."
          rows={5}
          compact
        />
      ) : (
        <Box sx={{ display: "grid", gap: 1.1 }}>
          <CandidateSection
            sectionKey="good_matches"
            title={sectionLabels.good_matches || "Best fits"}
            candidates={payload?.good_matches || []}
            sendingId={sendingId}
            onPrimaryPress={handlePrimaryPress}
          />
          <CandidateSection
            sectionKey="recently_active"
            title={sectionLabels.recently_active || "Active this week"}
            candidates={payload?.recently_active || []}
            sendingId={sendingId}
            onPrimaryPress={handlePrimaryPress}
          />
          <CandidateSection
            sectionKey="other_people"
            title={sectionLabels.other_people || "Browse more people"}
            candidates={payload?.other_people || []}
            sendingId={sendingId}
            onPrimaryPress={handlePrimaryPress}
          />
        </Box>
      )}

      {!loading && !error && !hasResults ? (
        <Box sx={cardShellSx({ mt: 2, px: 2.4, py: 2.6, textAlign: "center" })}>
          <Typography sx={{ fontSize: "1.15rem", fontWeight: 800, color: "#13233d" }}>
            No matches yet
          </Typography>
          <Typography sx={{ mt: 0.7, color: "#60738c" }}>
            Try a different search or invite someone you already know.
          </Typography>
          <Button
            variant="contained"
            onClick={onOpenInvite}
            sx={{
              mt: 1.6,
              minHeight: 42,
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 800,
              backgroundColor: trustBudTokens.colors.brand.navy700,
            }}
          >
            Invite by link
          </Button>
        </Box>
      ) : null}

      {payload?.has_more ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2.2 }}>
          <Button
            variant="outlined"
            onClick={() => loadDiscovery({ nextOffset: payload?.next_offset || 0, append: true })}
            endIcon={<ArrowForwardRoundedIcon />}
            sx={{ minHeight: 42, borderRadius: "14px", textTransform: "none", fontWeight: 800 }}
          >
            Load more
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}
